// Field options for the inspector.
//
// A node works on the schema of its incoming edges once the workflow has run.
// Before that first run the editor falls back to the fields of the graph's
// source node (entity plus joins), so parameters can be configured straight
// after a node has been dropped on the canvas.

import { SOURCE_TYPE, inputFields } from './graphModel';

export function sourceFields(entityCatalog, joinFields = {}) {
  const joined = Object.keys(joinFields || {}).reduce(
    (all, key) => all.concat(joinFields[key] || []),
    []
  );
  return [...((entityCatalog && entityCatalog.fields) || []), ...joined];
}

export function availableFields({ graph, result, nodeId, entityCatalog, joinFields }) {
  const upstream = inputFields(result, graph, nodeId);
  return upstream.length ? upstream : sourceFields(entityCatalog, joinFields);
}

export function sourceNodes(graph) {
  return ((graph && graph.nodes) || []).filter((node) => node.type === SOURCE_TYPE);
}

// Entities and joins used anywhere in the graph drive the catalogs we load.
export function graphEntities(graph) {
  const keys = sourceNodes(graph)
    .map((node) => node.params?.entity)
    .filter(Boolean);
  return Array.from(new Set(keys.length ? keys : ['grave']));
}

export function graphJoins(graph) {
  const keys = sourceNodes(graph).reduce((all, node) => all.concat(node.params?.joins || []), []);
  return Array.from(new Set(keys));
}

// Catalog fields say `kind: numeric|angle|enum|text`, result schemas say
// `type: number|text|boolean`; both shapes occur here.
export function filterByKind(fields, kind) {
  const list = fields || [];
  if (!kind) return list;
  if (kind === 'numeric') return list.filter(isNumeric);
  // Result schemas only know `number|text|boolean`, so everything that is not
  // a number can be grouped, colored or sorted by.
  if (kind === 'categorical') return list.filter((field) => !isNumeric(field));
  return list.filter((field) => (field.kind || field.type) === kind);
}

function isNumeric(field) {
  return field.kind === 'numeric' || field.kind === 'angle' || field.type === 'number';
}

// Limit the schema to the configured columns (all fields when none set).
export function pickFields(fields, columns) {
  const all = fields || [];
  if (!columns || !columns.length) return all;
  const picked = all.filter((field) => columns.includes(field.key));
  return picked.length ? picked : all;
}

export function labelOf(fields, key) {
  const field = (fields || []).find((f) => f.key === key);
  return field ? field.label || field.key : key;
}
